import React, { useCallback, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Layout as AntdLayout, Avatar, Icon, Select } from "antd";
import { Link, withRouter, NavLink, useRouteMatch } from "react-router-dom";
import { connect } from "react-redux";
import * as R from "ramda";
import { Menu, Tooltip } from "antd";
import { withTranslation } from "react-i18next";
import { compose } from "recompose";

import themes from "../../../utils/themes";
import { getOptimizedAvatar } from "../../../utils/images";
import logo from "../../../assets/logo-blue.svg";
import { logout } from "../../../store/actions/session";
import { selectCompany } from "../../../store/actions/company";
import { BREAKPOINTS } from "../../../constants/sizeBreakpoints";
import Button from "../../Button";

import Profile from "./Profile";
import Notifications from "./Notifications";
import { TopLink } from "./TopLink";

const Header = AntdLayout;

const Container = styled.div`
  margin-bottom: 64px;
  .header {
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 100;
    padding: 0 24px 0 40px !important;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    height: 64px;
    @supports (display: grid) {
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      min-height: 64px;
      height: auto;
    }
    align-items: center;
    background: ${({ custom }) =>
      custom && custom.color1 ? custom.color1 : "white"};
  }
  @media print {
    display: none;
  }
`;

export const Logo = styled.img`
  max-height: 24px;
  height: 24px;
  width: inherit;
`;

const ItemContainer = styled.div`
  display: flex;
  align-items: center;

  .ant-avatar {
    img {
      object-fit: cover;
    }
    margin-right: 8px;
  }

  .item-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const LeftNav = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
`;

const StyledSelect = styled(Select)`
  width: 220px;
  .ant-select-selection--single {
    border-color: transparent !important;
  }
`;

const StyledLink = styled(NavLink)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 0 8px;
  line-height: 1em;
  margin: 0 16px;
  white-space: nowrap;
  color: ${({ activecolor, theme }) =>
    activecolor ? theme["@primary"] : "#747c84"};

  position: relative;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 4px;
    transition: 0.25s ease all;
    background: ${({ theme }) => theme["@primary"]};
  }

  &:focus {
    text-decoration: none;
  }

  &.active {
    color: ${({ theme }) => theme["@primary"]};

    &:after {
      width: 100%;
    }
  }

  .anticon {
    font-size: 24px;
    cursor: pointer;
    margin-bottom: 4px;
  }

  // Prevent badge changing icon color
  .ant-badge {
    color: currentColor;
  }

  .ant-badge-count {
    font-size: 10px;
    padding: 0 4px;
  }

  @media (max-width: ${BREAKPOINTS.md}) {
    margin-right: 20px;
    .anticon {
      font-size: 32px;
    }
    &.active {
      color: ${({ theme }) => theme["@primary"]};
      &:after {
        height: 4px;
      }
    }
  }

  @media (max-width: ${BREAKPOINTS.xs}) {
    .anticon {
      font-size: 24px;
    }
  }
`;

const Center = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
`;

const RightNav = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  height: 100%;
  min-width: 120px;
  #HW_badge.HW_softHidden {
    width: 57px;
    height: 64px;
    opacity: 0;
  }
  #HW_badge {
    left: 40px;
    z-index: 10;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    color: #fff;
    font-weight: normal;
    font-size: 12px;
    line-height: 20px;
    white-space: nowrap;
    text-align: center;
    background: #f5222d;
    border-radius: 10px;
    box-shadow: 0 0 0 1px #fff;
  }
  #HW_badge_cont.HW_visible {
    width: 73px;
    height: 64px;
    top: -3px;
    right: 0;
  }
  #headway-changelog {
    position: relative;
  }

  #HW_badge_cont {
    position: absolute !important;
  }
`;

const TopBar = ({
  t,
  company,
  companies,
  user,
  headquarter,
  logout,
  isSuperadmin,
  isAdmin,
  selectCompany
}) => {
  const isHeadadmin = useMemo(() => R.isEmpty(company) && isSuperadmin, [
    company,
    isSuperadmin
  ]);
  const isOpportunityRoute = useRouteMatch("/oportunidades/:id");
  const isOpportunitiesRoute = useRouteMatch("/oportunidades");
  const isCandidateBaseRoute = useRouteMatch("/base-candidatos");
  const isTeamsRoute = useRouteMatch("/equipos");
  const isCompaniesRoute = useRouteMatch("/compañias");

  const hasCompanies = useMemo(
    () =>
      companies &&
      R.compose(
        R.gt(R.__, 1),
        R.length
      )(companies),
    [companies]
  );

  const headwayHasLoaded = typeof window.Headway !== "undefined";

  useEffect(() => {
    const config = {
      selector: "#headway-changelog", // CSS selector where to inject the badge
      account: "xWOBrx",
      translations: {
        title: t("headwayNotifications:title"),
        readMore: t("headwayNotifications:readMore"),
        labels: {
          new: t("headwayNotifications:labels.new"),
          improvement: t("headwayNotifications:labels.improvement"),
          fix: t("headwayNotifications:labels.fix")
        },
        footer: t("headwayNotifications:footer")
      }
    };

    if (headwayHasLoaded) window.Headway.init(config);
  }, [headwayHasLoaded, t]);

  const handleCompanyChange = useCallback(
    id => {
      if (hasCompanies) {
        selectCompany(R.find(R.propEq("id", id), companies));
      }
    },
    [companies, hasCompanies, selectCompany]
  );

  return (
    <Container custom={themes[company.email]}>
      <Header className="header" data-test="header">
        <LeftNav>
          <Link to="/">
            <Logo data-test="logo" src={logo} alt="Worcket" />
          </Link>
          <StyledLink
            exact
            activecolor={isOpportunityRoute ? 1 : undefined}
            to="/oportunidades"
          >
            <Icon type="shop" />
          </StyledLink>
          {isSuperadmin && company.id && hasCompanies && (
            <StyledSelect
              data-test={`company-selector-${company.id}`}
              onChange={handleCompanyChange}
              value={company.id}
            >
              {R.compose(
                R.map(({ id, name }) => (
                  <Select.Option key={id} value={id}>
                    {name}
                  </Select.Option>
                )),
                R.sortBy(R.prop("name"))
              )(companies)}
            </StyledSelect>
          )}
        </LeftNav>
        <Center>
          {!isCompaniesRoute && (
            <>
              {R.pathOr(true, ["config", "candidatesBase"], user) && (
                <Link to="/base-candidatos">
                  <Button
                    data-test="go-to-candidates-button"
                    type={isCandidateBaseRoute ? "primary" : "bordered"}
                  >
                    {t("breadcrumbs:candidatesBase")}
                  </Button>
                </Link>
              )}
              <Link to="/oportunidades" style={{ marginLeft: 10 }}>
                <Button
                  data-test="go-to-opportunities-button"
                  type={
                    isOpportunitiesRoute || isTeamsRoute
                      ? "primary"
                      : "bordered"
                  }
                >
                  {t("breadcrumbs:opportunities")}
                </Button>
              </Link>
            </>
          )}
        </Center>
        <RightNav headquarter={headquarter}>
          {headwayHasLoaded && (
            <TopLink
              id="headway-changelog"
              title={t("headwayNotifications:title")}
            >
              <Icon style={{ transform: "rotate(45deg)" }} type="rocket" />
            </TopLink>
          )}

          {company.id && <Notifications />}
          {isSuperadmin ? (
            <Tooltip
              placement="bottom"
              title={
                isHeadadmin
                  ? `${t("global:from")} ${headquarter.name}`
                  : `${t("global:from")} ${company.name}`
              }
            >
              <TopLink
                as={Link}
                to={"/analytics"}
                title={t("global:analytics")}
              >
                <Icon type="line-chart" />
              </TopLink>
            </Tooltip>
          ) : (
            isAdmin && (
              <TopLink
                as={Link}
                to={"/analytics"}
                title={t("global:analytics")}
              >
                <Icon type="line-chart" />
              </TopLink>
            )
          )}
          <Profile
            image={user.avatar}
            email={user.email}
            menu={
              <Menu
                onClick={({ key }) => {
                  if (key === "logout") {
                    logout();
                  }
                }}
              >
                {user.id && (
                  <Menu.Item key="user">
                    <Link to="/usuario/perfil">
                      <ItemContainer>
                        <Avatar
                          src={getOptimizedAvatar(user.avatar)}
                          size="small"
                          icon="user"
                        />
                        <span className="item-text">{user.name}</span>
                      </ItemContainer>
                    </Link>
                  </Menu.Item>
                )}
                {headquarter.id && (
                  <Menu.Item key="headquarter">
                    <Link to="/cuartel/perfil">
                      <ItemContainer>
                        <Avatar
                          src={getOptimizedAvatar(headquarter.logo)}
                          size="small"
                          icon="shop"
                        />
                        <span className="item-text">{headquarter.name}</span>
                      </ItemContainer>
                    </Link>
                  </Menu.Item>
                )}
                {company.id && (
                  <Menu.Item key="company">
                    <Link to="/compañia/perfil">
                      <ItemContainer>
                        <Avatar
                          src={getOptimizedAvatar(company.logo)}
                          size="small"
                          icon="shop"
                        />
                        <span className="item-text">{company.name}</span>
                      </ItemContainer>
                    </Link>
                  </Menu.Item>
                )}
                {headquarter.id && (
                  <Menu.Item key="companies">
                    <Link to="/compañias">{t("links:companies")}</Link>
                  </Menu.Item>
                )}
                <Menu.Divider />
                <Menu.Item key="logout">{t("links:logout")}</Menu.Item>
              </Menu>
            }
          />
        </RightNav>
      </Header>
    </Container>
  );
};

const enhancer = compose(
  withRouter,
  withTranslation(["global", "opportunities", "headwayNotifications"]),
  connect(
    R.applySpec({
      company: R.propOr({}, "company"),
      companies: R.prop("companies"),
      user: R.propOr({}, "user"),
      headquarter: R.propOr({}, "headquarter"),
      isSuperadmin: R.pipe(
        R.pathOr([], ["user", "roles"]),
        R.includes("headadmin")
      ),
      isAdmin: R.pipe(
        R.pathOr([], ["user", "roles"]),
        R.includes("admin")
      )
    }),
    { logout, selectCompany }
  )
);

export default enhancer(TopBar);
